import {forwardRef} from 'react';
import {cn} from '../lib/cn';
import {ErrorMessage} from '../error-message';
import {RadioGroup, RadioGroupItem} from '../radio-group';
import {useRadioOptions} from './useRadioOptions';
import {type RadioOption, type RadioProps} from './types';

export const Radio = forwardRef<HTMLDivElement, RadioProps>(
  (
    {
      options,
      value,
      onChange,
      error,
      className = '',
      size,
      disabled = false,
      renderOption,
    },
    ref,
  ) => {
    const {selectedValue, handleValueChange} = useRadioOptions({
      options,
      defaultValue: value,
      onChange,
    });

    const getIsSelected = (option: RadioOption) => {
      if (typeof selectedValue === 'object') {
        return option.value === selectedValue.value;
      }

      return option.value === selectedValue;
    };

    return (
      <>
        <RadioGroup
          ref={ref}
          value={typeof selectedValue === 'string' ? selectedValue : selectedValue?.value}
          onValueChange={handleValueChange}
          className={cn(className, 'radio')}
          disabled={disabled}
        >
          {options.map(option => (
            <label
              key={option.value}
              className={cn(
                'radio__wrapper',
                'flex cursor-pointer items-center gap-2 transition-all duration-200',
                (disabled || option.disabled) &&
                  'cursor-default opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              )}
            >
              {renderOption ? (
                renderOption({option, isSelected: getIsSelected(option)})
              ) : (
                <>
                  <RadioGroupItem
                    value={option.value}
                    id={`option-${option.value}`}
                    disabled={option.disabled}
                    size={size}
                    className="radio__indicator"
                  />
                  <p className="radio_label">{option.label}</p>
                </>
              )}
            </label>
          ))}
        </RadioGroup>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </>
    );
  },
);

Radio.displayName = 'Radio';
